import { useGlobalProperty } from '@/hooks/useGlobalProperty';
import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { propertyAPI, otaAPI } from '@/services/api';
import { Wifi, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const OTA_PLATFORMS = [
  { 
    name: 'Booking.com', 
    logo: 'https://logo.clearbit.com/booking.com',
    description: 'Líder mundial en reservas de alojamiento'
  },
  { 
    name: 'Airbnb', 
    logo: 'https://logo.clearbit.com/airbnb.com',
    description: 'Plataforma global de alquileres vacacionales'
  },
  { 
    name: 'Expedia', 
    logo: 'https://logo.clearbit.com/expedia.com',
    description: 'Agencia de viajes online líder'
  },
  { 
    name: 'Priceline', 
    logo: 'https://logo.clearbit.com/priceline.com',
    description: 'Reservas de hoteles con ofertas especiales'
  },
  { 
    name: 'Hotels.com', 
    logo: 'https://logo.clearbit.com/hotels.com',
    description: 'Red global de hoteles'
  },
  { 
    name: 'Despegar', 
    logo: 'https://logo.clearbit.com/despegar.com',
    description: 'OTA líder en Latinoamérica'
  }
];

export default function OTAConnectionsPage() {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useGlobalProperty(properties);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState({});

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    if (selectedProperty) {
      fetchConnections();
    }
  }, [selectedProperty]);

  const fetchProperties = async () => {
    try {
      const response = await propertyAPI.getAll();
      setProperties(response.data);
      if (response.data.length > 0) {
        
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Error al cargar propiedades');
    } finally {
      setLoading(false);
    }
  };

  const fetchConnections = async () => {
    try {
      const response = await otaAPI.getAll(selectedProperty);
      setConnections(response.data);
    } catch (error) {
      console.error('Error fetching connections:', error);
      toast.error('Error al cargar conexiones');
    }
  };

  const isConnected = (otaName) => {
    const connection = connections.find(c => c.ota_name === otaName);
    return connection?.conectado || false;
  };

  const handleToggleConnection = async (otaName, currentStatus) => {
    setToggling({ ...toggling, [otaName]: true });
    
    try {
      await otaAPI.create({
        property_id: selectedProperty,
        ota_name: otaName,
        conectado: !currentStatus
      });
      
      toast.success(
        !currentStatus 
          ? `¡Conectado a ${otaName}!` 
          : `Desconectado de ${otaName}`
      );
      
      fetchConnections();
    } catch (error) {
      console.error('Error toggling connection:', error);
      toast.error('Error al cambiar conexión');
    } finally {
      setToggling({ ...toggling, [otaName]: false });
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Cargando conexiones...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const connectedCount = OTA_PLATFORMS.filter(ota => isConnected(ota.name)).length;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="ota-connections-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold" style={{fontFamily: 'Manrope'}} data-testid="ota-connections-title">
              Conexiones OTA
            </h1>
            <p className="text-muted-foreground mt-1">
              Conecta tus canales de distribución
            </p>
          </div>

          {properties.length > 0 && (
            <Select value={selectedProperty} onValueChange={setSelectedProperty}>
              <SelectTrigger className="w-full sm:w-[280px]" data-testid="ota-property-selector">
                <SelectValue placeholder="Selecciona una propiedad" />
              </SelectTrigger>
              <SelectContent>
                {properties.map((prop) => (
                  <SelectItem key={prop.id} value={prop.id}>
                    {prop.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Stats Card */}
        <Card className="bg-primary text-white">
          <CardContent className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold mb-1" style={{fontFamily: 'Manrope'}}>
                  OTAs Conectadas
                </h3>
                <p className="text-white/80">
                  {connectedCount} de {OTA_PLATFORMS.length} plataformas activas
                </p>
              </div>
              <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center">
                <Wifi className="h-8 w-8" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Info Alert */}
        <Card className="bg-accent/50 border-accent-foreground/20">
          <CardContent className="py-4">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-accent-foreground flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold mb-1">Modo Demo</p>
                <p className="text-muted-foreground">
                  Las conexiones actuales están en modo simulación. Para conectar con las APIs reales de las OTAs, 
                  necesitarás obtener credenciales de cada plataforma. Contacta con nuestro equipo de soporte 
                  para ayudarte con el proceso de integración.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* OTA Platforms Grid */}
        <div className="grid gap-4 md:grid-cols-2">
          {OTA_PLATFORMS.map((ota) => {
            const connected = isConnected(ota.name);
            const isToggling = toggling[ota.name];
            
            return (
              <Card key={ota.name} className="hover:shadow-md transition-shadow duration-200">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center overflow-hidden">
                        <img 
                          src={ota.logo} 
                          alt={ota.name}
                          className="w-8 h-8 object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = `<span class="text-lg font-bold">${ota.name.charAt(0)}</span>`;
                          }}
                        />
                      </div>
                      <div>
                        <CardTitle className="text-base" style={{fontFamily: 'Manrope'}}>
                          {ota.name}
                        </CardTitle>
                        {connected && (
                          <Badge variant="outline" className="mt-1">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Conectado
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    <Switch
                      checked={connected}
                      onCheckedChange={() => handleToggleConnection(ota.name, connected)}
                      disabled={isToggling}
                      data-testid={`ota-toggle-${ota.name.toLowerCase().replace(/\./g, '-')}`}
                    />
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription>{ota.description}</CardDescription>
                  
                  {connected && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Última sincronización:</span>
                        <span className="font-medium">Hace 5 minutos</span>
                      </div>
                      <div className="flex items-center justify-between text-sm mt-2">
                        <span className="text-muted-foreground">Estado:</span>
                        <span className="flex items-center gap-1 text-green-600">
                          <CheckCircle className="h-4 w-4" />
                          Activo
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Instructions Card */}
        <Card>
          <CardHeader>
            <CardTitle style={{fontFamily: 'Manrope'}}>
              Cómo Obtener Credenciales de OTA
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">Booking.com</h4>
              <p className="text-sm text-muted-foreground">
                1. Accede a Extranet de Booking.com<br />
                2. Ve a "Conectividad" &gt; "Channel Manager"<br />
                3. Solicita acceso a la API de Conectividad<br />
                4. Recibirás tus credenciales por email
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-2">Airbnb</h4>
              <p className="text-sm text-muted-foreground">
                1. Inicia sesión en tu cuenta de Airbnb<br />
                2. Ve a "Cuenta" &gt; "Configuración profesional"<br />
                3. Busca "Integración con software"<br />
                4. Solicita acceso a la API para tu PMS
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Expedia</h4>
              <p className="text-sm text-muted-foreground">
                1. Accede a Expedia Partner Central<br />
                2. Ve a "Conectividad"<br />
                3. Registra tu PMS como proveedor de tecnología<br />
                4. Completa el proceso de certificación
              </p>
            </div>

            <div className="bg-muted rounded-lg p-4 mt-6">
              <p className="text-sm">
                <strong>Nota:</strong> El proceso de obtención de credenciales puede tomar de 2 a 4 semanas. 
                Nuestro equipo puede asistirte durante todo el proceso. 
                <Button variant="link" className="px-1 h-auto" data-testid="contact-support-btn">
                  Contactar soporte
                </Button>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
